// gallery-listing.js
import Splide from '@splidejs/splide';
import { toggleLoader, updateURL, setGalleryView, addNavigationButtons, scrollToGalleryTop } from './gallery-functions.js';
import { initGalleryHome } from './gallery-home.js';
import { initGallerySidebar } from './gallery-sidebar.js';
import { initGallerySingle } from './gallery-single.js';

let currentProcedureName = '';
let currentPage = 1;
let isLoading = false;
let hasMorePatients = true;
let currentProcedureId = null;
let galleryStyle = 'slider';

export function initGalleryListing(procedureId, isParent, procedureName, page = 1) {
    setGalleryView('listing');
    
    document.getElementById('gallery-main').style.display = 'none';

    // Show the sidebar
    const sidebar = document.querySelector('.gallery-sidebar');
    if (sidebar) {
        sidebar.style.display = 'block';
    }
    
    // Get the gallery style from PHP
    const formData = new URLSearchParams();
    formData.append('action', 'get_gallery_style');
    
    fetch(ajaxurl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData
    })
    .then(response => response.json())
    .then(response => {
        if (response.success) {
            galleryStyle = response.data.style || 'slider';
            loadPatientsByProcedure(procedureId, isParent, procedureName, page);
        } else {
            console.error('Failed to get gallery style:', response);
            // Default to slider style and continue loading
            galleryStyle = 'slider';
            loadPatientsByProcedure(procedureId, isParent, procedureName, page);
        }
    })
    .catch(error => {
        console.error('Error getting gallery style:', error);
        // Default to slider style and continue loading
        galleryStyle = 'slider';
        loadPatientsByProcedure(procedureId, isParent, procedureName, page);
    });
}

function getProcedureUrl(procedureId) {
    return new Promise((resolve, reject) => {
        const formData = new URLSearchParams();
        formData.append('action', 'get_procedure_url');
        formData.append('procedure_id', procedureId);
        
        fetch(ajaxurl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: formData
        })
        .then(response => response.json())
        .then(response => {
            if (response.success && response.data.url) {
                resolve(response.data.url);
            } else {
                resolve(null); // Return null if no URL found
            }
        })
        .catch(error => {
            console.error('Error getting procedure URL:', error);
            resolve(null);
        });
    });
}

function loadPatientsByProcedure(procedureId, isParent, procedureName, page = 1, append = false) {
    if (isLoading) return;
    isLoading = true;
    currentProcedureId = procedureId;
    currentProcedureName = procedureName;
    
    // Get the gallery style from PHP
    const formData = new URLSearchParams();
    formData.append('action', 'get_gallery_style');
    
    fetch(ajaxurl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData
    })
    .then(response => response.json())
    .then(response => {
        if (response.success) {
            galleryStyle = response.data.style || 'slider';
            loadPatientsByProcedure(procedureId, isParent, procedureName, page);
        } else {
            console.error('Failed to get gallery style:', response);
            // Default to slider style and continue loading
            galleryStyle = 'slider';
            loadPatientsByProcedure(procedureId, isParent, procedureName, page);
        }
    })
    .catch(error => {
        console.error('Error getting gallery style:', error);
        // Default to slider style and continue loading
        galleryStyle = 'slider';
        loadPatientsByProcedure(procedureId, isParent, procedureName, page);
    });

    if (!append) {
        scrollToGalleryTop();
        initGallerySidebar(procedureId);
    }
    
    toggleLoader(true, append);

    // Create FormData object and append all necessary data
    const patientsFormData = new URLSearchParams();
    patientsFormData.append('action', isParent ? 'get_patients_by_parent_category' : 'get_patients_by_procedure');
    patientsFormData.append(isParent ? 'parent_id' : 'procedure_id', procedureId);
    patientsFormData.append('page', page);

    fetch(ajaxurl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: patientsFormData
    })
    .then(response => response.json())
    .then(response => {
        toggleLoader(false, append);
        isLoading = false;

        if (response.success && response.data.patients && response.data.patients.length > 0) {
            renderPatients(response.data.patients, page, append);
            updateURL({procedureId: procedureId, page: page});
            currentPage = page;
            hasMorePatients = response.data.has_more;
            
            if (!append) {
                getProcedureUrl(procedureId).then(procedureUrl => {
                    addNavigationButtons('Back to gallery home', procedureUrl, initGalleryHome);
                });
            }

            if (window.initMobileSidebar) {
                window.initMobileSidebar();
            }

        } else if (page === 1) {
            const patientsContainer = document.getElementById('patients-container');
            patientsContainer.innerHTML = `
                <div class="gallery gallery--empty">
                    <h1>${currentProcedureName} <span>Before & After Photos</span></h1>
                    <p>There are no patients under this procedure.</p>
                </div>
            `;
            
            getProcedureUrl(procedureId).then(procedureUrl => {
                addNavigationButtons('Back to gallery home', procedureUrl, initGalleryHome);
            });
            
            updateURL({procedureId: procedureId});
            hasMorePatients = false;
        } else {
            hasMorePatients = false;
        }
        
        if (!append) {
            window.addEventListener('scroll', handleScroll);
        }
    })
    .catch(error => {
        console.error('Error loading patients:', error);
        toggleLoader(false, append);
        isLoading = false;
        
        const patientsContainer = document.getElementById('patients-container');
        patientsContainer.innerHTML = `
            <div class="gallery gallery--error">
                <h1>${currentProcedureName} <span>Before & After Photos</span></h1>
                <p>There was an error loading the gallery. Please try again.</p>
            </div>
        `;
        
        getProcedureUrl(procedureId).then(procedureUrl => {
            addNavigationButtons('Back to gallery home', procedureUrl, initGalleryHome);
        });
    });
}

function renderPatients(patients, page, append) {
    const patientsContainer = document.getElementById('patients-container');
    
    // Only create the structure if this is not an append operation
    if (!append) {
        const structureHtml = `
            <div class="gallery gallery--listing gallery--${galleryStyle}">
                <h1>${currentProcedureName} <span>Before & After Photos</span></h1>
                <div class="gallery__cases"></div>
            </div>
        `;
        
        patientsContainer.innerHTML = structureHtml;
    }

    // Get reference to cases container
    const casesElement = document.querySelector('.gallery__cases');
    let casesHtml = '';

    // Sort patients if they have patient_order
    const sortedPatients = [...patients].sort((a, b) => {
        // If both have patient_order, sort by that
        if (a.patient_order !== null && b.patient_order !== null) {
            return a.patient_order - b.patient_order;
        }
        // If only one has patient_order, put the one with order first
        if (a.patient_order !== null) return -1;
        if (b.patient_order !== null) return 1;
        // If neither has patient_order, maintain original order
        return 0;
    });

    // Use different rendering based on gallery style
    if (galleryStyle === 'slider') {
        sortedPatients.forEach(patient => {
            casesHtml += renderPatientSlider(patient);
        });
    } else {
        sortedPatients.forEach((patient, index) => {
            casesHtml += renderPatientDefault(patient, index, page);
        });
    }

    if (append) {
        casesElement.insertAdjacentHTML('beforeend', casesHtml);
    } else {
        casesElement.innerHTML = casesHtml;
    }

    // Initialize sliders after content is added
    if (galleryStyle === 'slider') {
        initializeSliders();
    }

    // Add back to top button if needed
    if (page > 1 && !document.querySelector('.back-to-top-btn')) {
        addBackToTopButton(casesElement);
    }
}


function renderPatientSlider(patient) {
    const caseType = patient.case_type || 'double';
    let photos = patient.photos || [];
    
    // Limit to 6 photos
    photos = photos.slice(0, 6);

    let html = `
        <div class="gallery__cases__case gallery__cases__case--${caseType}" data-id="${patient.id}" data-procedure-id="${currentProcedureId}">
            <div class="patients-slider splide" aria-label="Before & After Slider">
                <div class="splide__track">
                    <ul class="splide__list">
    `;

    if (photos.length > 0) {
        photos.forEach((photo, index) => {
            if (caseType === 'double') {
                if (index % 2 === 0) {
                    html += '<li class="splide__slide">';
                }

                html += `
                    <div data-id="${patient.id}">
                        <img src="${photo.url}" alt="${photo.alt || `${currentProcedureName} case #${patient.id}`}">
                        <p>${!patient.disable_labels && photo.label || (index % 2 === 0 ? 'Before' : 'After')}</p>
                    </div>
                `;

                if (index % 2 !== 0 || index === photos.length - 1) {
                    html += '</li>';
                }
            } else {
                // For single case type, show one image per slide
                html += `
                    <li class="splide__slide">
                        <div class="single-photo" data-id="${patient.id}">
                            <img src="${photo.url}" alt="${photo.alt || `${currentProcedureName} case #${patient.id}`}">
                            <p>${!patient.disable_labels && photo.label || ''}</p>
                        </div>
                    </li>
                `;
            }
        });
    }

    html += `
                </ul>
            </div>
        </div>

        <div class="patients-thumbnail-slider splide">
            <div class="splide__track">
                <ul class="splide__list">
    `;

    if (photos.length > 0) {
        photos.forEach((photo, index) => {
            if (caseType === 'double') {
                if (index % 2 === 0) {
                    html += '<li class="splide__slide">';
                }

                html += `
                    <img src="${photo.url}" alt="${photo.alt || `${currentProcedureName} case #${patient.id} thumbnail`}">
                `;

                if (index % 2 !== 0 || index === photos.length - 1) {
                    html += '</li>';
                }
            } else {
                html += `
                    <li class="splide__slide">
                        <img src="${photo.url}" alt="${photo.alt || `${currentProcedureName} case #${patient.id} thumbnail`}">
                    </li>
                `;
            }
        });
    }

    html += `
                </ul>
            </div>
        </div>

        <p class="text-center">
            <button 
                type="button"
                class="btn btn--outline view-patient-btn" 
                data-id="${patient.id}"
                data-procedure-id="${currentProcedureId}"
                aria-label="View ${currentProcedureName} case ${patient.id}"
            >
                View Details
            </button>
        </p>
    </div>`;

    return html;
}

function renderPatientDefault(patient, index, page) {
    let html = `
        <div class="patient" data-id="${patient.id}" data-procedure-id="${currentProcedureId}">
            <h3>Case #${patient.id}</h3>`;
    
    if (patient.photos && patient.photos.length > 0) {
        html += '<div class="patient-photos">';
        // Simply limit to first 4 photos
        patient.photos.slice(0, 4).forEach(function(photo) {
            html += `
                <div class="patient-photo">
                    <img src="${photo.url}" alt="${photo.alt || `${currentProcedureName} case #${patient.id}`}">
                    ${photo.label ? `<p class="patient-photo-label">${photo.label}</p>` : ''}
                </div>`;
        });
        html += '</div>';
    }

    html += `
            <p>
                <button 
                    type="button"
                    class="btn btn--outline view-patient-btn" 
                    data-id="${patient.id}"
                    data-procedure-id="${currentProcedureId}"
                    aria-label="View ${currentProcedureName} case ${patient.id}"
                >
                    View Details
                </button>
            </p>
        </div>`;

    // Add back to top button after the 5th item on pages after the first
    if (page > 1 && index === 4) {
        html += '<button class="back-to-top-btn">Back to Top</button>';
    }

    return html;
}

// Add this function to handle the button clicks
function attachPatientViewHandlers() {
    document.querySelectorAll('.view-patient-btn').forEach(button => {
        button.addEventListener('click', () => {
            const patientId = button.dataset.id;
            const procedureId = button.dataset.procedureId;
            window.initGallerySingle(patientId, procedureId, currentProcedureName);
        });
    });
}

// Function to initialize Splide sliders
function initializeSliders() {
    // Get all cases and iterate through them
    document.querySelectorAll('.gallery__cases__case').forEach(item => {
        const patientsSlider = item.querySelector('.patients-slider');
        const thumbnailSlider = item.querySelector('.patients-thumbnail-slider');
        
        if (patientsSlider) {
            const main = new Splide(patientsSlider, {
                type: 'fade',
                rewind: true,
                pagination: false,
                arrows: true
            });
            
            const thumbnails = new Splide(thumbnailSlider, {
                fixedWidth: 100,
                fixedHeight: 60,
                rewind: true,
                pagination: false,
                arrows: false,
                isNavigation: true,
                breakpoints: {
                    600: {
                        fixedWidth: 60,
                        fixedHeight: 44,
                    },
                },
            });
            
            main.sync(thumbnails);
            main.mount();
            thumbnails.mount();
        }
    });

    // Attach click handlers to view details buttons
    document.querySelectorAll('.view-patient-btn').forEach(button => {
        button.addEventListener('click', () => {
            const patientId = button.dataset.id;
            const procedureId = button.dataset.procedureId;
            initGallerySingle(patientId, procedureId, currentProcedureName);
        });
    });
}

function handleScroll() {
    const backToTopBtn = document.querySelector('.back-to-top-btn');
    if (backToTopBtn) {
        const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
        const galleryContainer = document.getElementById('gallery-procedures-container');
        const containerTop = galleryContainer?.offsetTop || 0;

        backToTopBtn.style.display = scrollPosition > containerTop ? 'flex' : 'none';
    }

    // Infinite scroll
    if (hasMorePatients && !isLoading) {
        const lastCase = document.querySelector('.gallery__cases__case:last-child');
        if (lastCase) {
            const rect = lastCase.getBoundingClientRect();
            const isNearBottom = rect.bottom <= window.innerHeight + 300; // 300px threshold
            
            if (isNearBottom) {
                loadPatientsByProcedure(currentProcedureId, false, currentProcedureName, currentPage + 1, true);
            }
        }
    }
}

function addBackToTopButton(container) {
    const backToTopButton = document.createElement('button');
    backToTopButton.className = 'back-to-top-btn';
    backToTopButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 15l-6-6-6 6"/></svg>Back to Top';
    container.appendChild(backToTopButton);
}

function shouldAddBackToTopButton(page, index) {
    return page > 1 && index === 4 && !document.querySelector('.back-to-top-btn');
}

function renderBackToTopButton() {
    return `
        <button class="back-to-top-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 15l-6-6-6 6"/>
            </svg>
            Back to Top
        </button>
    `;
}

export function removeScrollListener() {
    window.removeEventListener('scroll', handleScroll);
}